import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { referralsAPI } from '../services/api';
import { ArrowLeft, Send } from 'lucide-react';

const NewReferral = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    patient_dob: '',
    procedure: '',
    urgency: 'routine',
    insurance: '',
    insurance_id: '',
    notes: '',
    medical_history: ''
  });

  const procedures = [
    'Wisdom Teeth Extraction',
    'Dental Implant',
    'Bone Grafting',
    'TMJ Treatment',
    'Jaw Surgery Consultation',
    'Tooth Extraction',
    'Pre-Prosthetic Surgery',
    'Oral Pathology',
    'Corrective Jaw Surgery',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await referralsAPI.create(formData);
      
      if (response.success) {
        toast({
          title: "Referral Submitted Successfully!",
          description: `Referral ID: ${response.referral.referral_id} has been created.`,
        });
        navigate('/referrals');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create referral');
      toast({
        title: "Error",
        description: err.response?.data?.detail || 'Failed to create referral',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div>
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/referrals')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Referrals
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-[#0A1628]">New Referral</CardTitle>
            <CardDescription>
              Submit a new patient referral for specialist evaluation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Patient Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#0A1628] border-b pb-2">
                  Patient Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_name">Full Name *</Label>
                    <Input
                      id="patient_name"
                      placeholder="John Doe"
                      value={formData.patient_name}
                      onChange={(e) => handleChange('patient_name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="patient_dob">Date of Birth *</Label>
                    <Input
                      id="patient_dob"
                      type="date"
                      value={formData.patient_dob}
                      onChange={(e) => handleChange('patient_dob', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="patient_email">Email *</Label>
                    <Input
                      id="patient_email"
                      type="email"
                      placeholder="patient@email.com"
                      value={formData.patient_email}
                      onChange={(e) => handleChange('patient_email', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="patient_phone">Phone Number *</Label>
                    <Input
                      id="patient_phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.patient_phone}
                      onChange={(e) => handleChange('patient_phone', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Insurance Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#0A1628] border-b pb-2">
                  Insurance Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="insurance">Insurance Provider</Label>
                    <Input
                      id="insurance"
                      placeholder="e.g., Blue Cross Blue Shield"
                      value={formData.insurance}
                      onChange={(e) => handleChange('insurance', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insurance_id">Insurance ID</Label>
                    <Input
                      id="insurance_id"
                      placeholder="Insurance ID Number"
                      value={formData.insurance_id}
                      onChange={(e) => handleChange('insurance_id', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Referral Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#0A1628] border-b pb-2">
                  Referral Details
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="procedure">Procedure/Treatment *</Label>
                    <Select
                      value={formData.procedure}
                      onValueChange={(value) => handleChange('procedure', value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select procedure" />
                      </SelectTrigger>
                      <SelectContent>
                        {procedures.map((proc) => (
                          <SelectItem key={proc} value={proc}>
                            {proc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="urgency">Urgency Level *</Label>
                    <Select
                      value={formData.urgency}
                      onValueChange={(value) => handleChange('urgency', value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="routine">Routine</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medical_history">Relevant Medical History</Label>
                  <Textarea
                    id="medical_history"
                    placeholder="Any relevant medical conditions, medications, allergies..."
                    value={formData.medical_history}
                    onChange={(e) => handleChange('medical_history', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Clinical Notes *</Label>
                  <Textarea
                    id="notes"
                    placeholder="Reason for referral, clinical findings, X-ray observations, treatment recommendations..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    required
                    rows={4}
                  />
                </div>
              </div>

              {/* Referring Doctor Info (auto-filled) */}
              {user?.role === 'referring_doctor' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#0A1628] border-b pb-2">
                    Referring Provider
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Name</p>
                    <p className="font-medium text-[#0A1628]">{user.name}</p>
                    <p className="text-sm text-gray-600 mt-3 mb-1">Practice</p>
                    <p className="font-medium text-[#0A1628]">{user.practice}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                >
                  {loading ? 'Submitting...' : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Referral
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/referrals')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewReferral;
